import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { 
  Briefcase, 
  FileText, 
  MapPin, 
  Clock, 
  DollarSign, 
  Building, 
  Calendar,
  ChevronRight,
  CheckCircle,
  XCircle,
  Hourglass,
  AlertCircle,
  Filter,
  Search,
  RefreshCw,
  Star,
  TrendingUp,
  Video
} from 'lucide-react'
import { apiClient } from '@/lib/api'

interface Application {
  uuid: string
  position_id?: number
  position_uuid?: string
  position_title: string
  company_name: string
  applied_date: string
  status: string
  location: string
  salary_range: string
  recruiter_name: string
  recruiter_avatar?: string
  last_update: string
  interview_scheduled?: boolean
}

export default function CandidateApplicationsPage() {
  const [applications, setApplications] = useState<Application[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filterStatus, setFilterStatus] = useState<string>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [currentUser, setCurrentUser] = useState<any>(null)

  useEffect(() => {
    // Load user data
    const userData = localStorage.getItem('currentUser')
    if (userData) {
      try {
        const user = JSON.parse(userData)
        setCurrentUser(user)
        loadApplications(user.email)
      } catch (error) {
        console.error('Error parsing user data:', error)
        setError('Unable to load user data')
        setLoading(false)
      }
    } else {
      setError('Please log in to view your applications')
      setLoading(false)
    }
  }, [])

  const loadApplications = async (email?: string) => {
    try {
      setLoading(true)
      setError(null)
      
      const response = await apiClient.getCandidateApplications(email)
      
      if (response.success && response.data?.applications) {
        // Ensure applications is an array and filter out invalid entries
        const validApplications = Array.isArray(response.data.applications) 
          ? response.data.applications.filter(app => app && app.uuid)
          : []
        setApplications(validApplications)
      } else if (response.error) {
        setError(typeof response.error === 'string' ? response.error : 'Failed to load applications')
      } else {
        setApplications([])
      }
    } catch (error) {
      console.error('Failed to load applications:', error)
      setError(typeof error === 'string' ? error : 'Failed to load applications. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleRefresh = () => {
    if (currentUser?.email) {
      loadApplications(currentUser.email)
    }
  }


  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'hired':
      case 'accepted':
      case 'offered':
        return 'bg-green-100 text-green-800'
      case 'rejected':
      case 'declined':
        return 'bg-red-100 text-red-800'
      case 'interview':
      case 'interviewing':
      case 'phone_screen':
      case 'technical_interview':
        return 'bg-blue-100 text-blue-800'
      case 'under_review':
      case 'reviewing':
      case 'shortlisted':
        return 'bg-yellow-100 text-yellow-800'
      case 'applied':
      case 'submitted':
        return 'bg-gray-100 text-gray-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'hired':
      case 'accepted':
      case 'offered':
        return <CheckCircle className="w-4 h-4" />
      case 'rejected':
      case 'declined':
        return <XCircle className="w-4 h-4" />
      case 'interview':
      case 'interviewing':
      case 'phone_screen':
      case 'technical_interview':
        return <Video className="w-4 h-4" />
      case 'under_review':
      case 'reviewing':
      case 'shortlisted':
        return <Hourglass className="w-4 h-4" />
      case 'applied':
      case 'submitted':
        return <FileText className="w-4 h-4" />
      default:
        return <AlertCircle className="w-4 h-4" />
    }
  }

  const formatDate = (dateString: string) => {
    try {
      if (!dateString) return 'N/A'
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    } catch {
      return dateString || 'N/A'
    }
  }

  const formatSalary = (salaryRange: string) => {
    if (!salaryRange || salaryRange === '0 - 0 USD') {
      return 'Salary not disclosed'
    }
    return salaryRange
  }

  const filteredApplications = applications.filter(app => {
    if (!app || !app.uuid) return false
    const status = app.status || 'applied'
    const title = app.position_title || ''
    const company = app.company_name || ''
    
    const matchesStatus = filterStatus === 'all' || status.toLowerCase().includes(filterStatus.toLowerCase())
    const matchesSearch = !searchQuery || 
      title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      company.toLowerCase().includes(searchQuery.toLowerCase())
    return matchesStatus && matchesSearch
  })

  const getApplicationStats = () => {
    const total = applications.length
    const pending = applications.filter(app => 
      app && app.status && ['applied', 'submitted', 'under_review', 'reviewing'].includes(app.status.toLowerCase())
    ).length
    const interviewing = applications.filter(app => 
      app && app.status && ['interview', 'interviewing', 'phone_screen', 'technical_interview'].includes(app.status.toLowerCase())
    ).length
    const hired = applications.filter(app => 
      app && app.status && ['hired', 'accepted', 'offered'].includes(app.status.toLowerCase())
    ).length
    const rejected = applications.filter(app => 
      app && app.status && ['rejected', 'declined'].includes(app.status.toLowerCase())
    ).length

    return { total, pending, interviewing, hired, rejected }
  }

  const stats = getApplicationStats()

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your applications...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Unable to Load Applications</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button 
            onClick={handleRefresh}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 inline-flex items-center"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Try Again
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">My Applications</h1>
          <p className="text-gray-600 mt-1">Track the progress of your job applications</p>
        </div>
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-4"
          >
            <div className="flex items-center">
              <Briefcase className="w-5 h-5 text-blue-600 mr-2" />
              <div>
                <p className="text-sm font-medium text-gray-600">Total</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-4"
          >
            <div className="flex items-center">
              <Hourglass className="w-5 h-5 text-yellow-600 mr-2" />
              <div>
                <p className="text-sm font-medium text-gray-600">Pending</p>
                <p className="text-2xl font-bold text-gray-900">{stats.pending}</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-4"
          >
            <div className="flex items-center">
              <Video className="w-5 h-5 text-blue-600 mr-2" />
              <div>
                <p className="text-sm font-medium text-gray-600">Interviewing</p>
                <p className="text-2xl font-bold text-gray-900">{stats.interviewing}</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-4"
          >
            <div className="flex items-center">
              <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
              <div>
                <p className="text-sm font-medium text-gray-600">Hired</p>
                <p className="text-2xl font-bold text-gray-900">{stats.hired}</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-4"
          >
            <div className="flex items-center">
              <XCircle className="w-5 h-5 text-red-600 mr-2" />
              <div>
                <p className="text-sm font-medium text-gray-600">Rejected</p>
                <p className="text-2xl font-bold text-gray-900">{stats.rejected}</p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Filters and Search */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by position or company..."
                className="pl-10 pr-4 py-3 w-full border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900"
              />
            </div>
            
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900"
            >
              <option value="all">All Statuses</option>
              <option value="applied">Applied</option>
              <option value="under_review">Under Review</option>
              <option value="interview">Interviewing</option>
              <option value="hired">Hired</option>
              <option value="rejected">Rejected</option>
            </select>

            <div className="flex items-center justify-end text-sm text-gray-600">
              <Briefcase className="w-4 h-4 mr-2" />
              {filteredApplications.length} applications found
            </div>
          </div>
        </div>

        {/* Applications List */}
        {filteredApplications.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <Briefcase className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-medium text-gray-900 mb-2">
              {applications.length === 0 ? 'No Applications Yet' : 'No Applications Found'}
            </h3>
            <p className="text-gray-600 mb-6">
              {applications.length === 0 
                ? 'Start applying to jobs to track your progress here.'
                : 'Try adjusting your search criteria or filters.'
              }
            </p>
            {applications.length === 0 && (
              <Link to="/jobs" className="btn-primary">
                Browse Jobs
              </Link>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {filteredApplications.map((application, index) => (
              <motion.div
                key={application.uuid}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow"
              >
                <div className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center mb-3">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(application.status || 'applied')} mr-3`}>
                          {getStatusIcon(application.status || 'applied')}
                          <span className="ml-1 capitalize">{(application.status || 'applied').replace('_', ' ')}</span>
                        </span>
                        {application.interview_scheduled && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            <Video className="w-3 h-3 mr-1" />
                            Interview Scheduled
                          </span>
                        )}
                      </div>
                      
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">
                        {application.position_uuid ? (
                          <Link
                            to={`/jobs/${application.position_uuid}`}
                            state={{
                              fromApplications: true,
                              applicationStatus: application.status
                            }}
                            className="hover:text-blue-600"
                          >
                            {application.position_title || 'Position Title'}
                          </Link>
                        ) : (
                          <span className="text-gray-900">
                            {application.position_title || 'Position Title'}
                          </span>
                        )}
                      </h3>
                      
                      <div className="flex flex-wrap items-center text-gray-600 text-sm mb-3 gap-4">
                        <div className="flex items-center">
                          <Building className="w-4 h-4 mr-1" />
                          <span className="font-medium">{application.company_name || 'Company'}</span>
                        </div>
                        {application.location && (
                          <div className="flex items-center">
                            <MapPin className="w-4 h-4 mr-1" />
                            <span>{application.location}</span>
                          </div>
                        )}
                        <div className="flex items-center">
                          <DollarSign className="w-4 h-4 mr-1" />
                          <span>{formatSalary(application.salary_range)}</span>
                        </div>
                      </div>

                      <div className="flex items-center text-sm text-gray-500 mb-4">
                        <Calendar className="w-4 h-4 mr-1" />
                        <span>Applied on {formatDate(application.applied_date)}</span>
                        <span className="mx-2">•</span>
                        <Clock className="w-4 h-4 mr-1" />
                        <span>Last updated {formatDate(application.last_update)}</span>
                      </div>

                      {application.recruiter_name && (
                        <div className="flex items-center text-sm text-gray-600">
                          <div className="w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center mr-2">
                            {application.recruiter_avatar ? (
                              <img 
                                src={application.recruiter_avatar} 
                                alt={application.recruiter_name}
                                className="w-6 h-6 rounded-full"
                              />
                            ) : (
                              <span className="text-xs font-medium">
                                {application.recruiter_name.split(' ').map(n => n[0]).join('')}
                              </span>
                            )}
                          </div>
                          <span>Recruiter: {application.recruiter_name}</span>
                        </div>
                      )}
                    </div>

                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Helpful Tips */}
        {applications.length > 0 && (
          <div className="mt-12 bg-blue-50 rounded-lg p-6">
            <div className="flex items-start">
              <TrendingUp className="w-6 h-6 text-blue-600 mr-3 mt-1" />
              <div>
                <h4 className="text-lg font-semibold text-blue-900 mb-2">Application Tips</h4>
                <ul className="text-blue-800 space-y-1 text-sm">
                  <li>• Follow up on applications that have been pending for more than a week</li>
                  <li>• Keep your resume and profile updated with latest skills and experience</li>
                  <li>• Prepare for interviews by researching the company and role</li>
                  <li>• Set up job alerts to stay notified about new opportunities</li>
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
